import { useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import AddIdea from "../../assets/task/idea-svgrepo-com.svg";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import { showError, showSuccess } from "../../components/common/ToastNotification";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import { InputWithAttachments } from "../../components/common/InputWithAttachments";
import { uploadFileDirectly } from "../projects/AddProject";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { GET_ALL_SKILLS, GET_CLIMBS, GET_IMPACT_LEVELS, PROJECTS_BY_EMP_ID_IDEA_BOX } from "../../api/query";
import { addMonths, addQuarters, addWeeks, endOfDay, endOfMonth, endOfQuarter, endOfWeek, format } from "date-fns";
import { CREATE_IDEA_GOAL } from "../../api/mutation";
import InputsDropdownMultipleSearchSelect from "../../components/common/InputsDropdownMultipleSearchSelect";
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";

const timelineOptions = [
    {
        key: 1,
        name: "This Week"
    },
    {
        key: 2,
        name: "Next Week"
    },
    {
        key: 3,
        name: "This Month"
    },
    {
        key: 4,
        name: "Next Month"
    },
    {
        key: 5,
        name: "This Quarter"
    },
]

const getPriorityClass = (priority: string) => {
    switch (priority?.toLowerCase()) {
        case "low":
            return "low-priority";
        case "medium":
            return "medium-priority";
        case "high":
            return "high-priority";
        default:
            return "text-gray-600";
    }
};

const getExpectedClosingDate = (timeline: string) => {
    const now = new Date();

    const dateMap: any = {
        "This Week": endOfWeek(now, { weekStartsOn: 1 }),
        "Next Week": endOfWeek(addWeeks(now, 1), { weekStartsOn: 1 }),
        "This Month": endOfMonth(now),
        "Next Month": endOfMonth(addMonths(now, 1)),
        "This Quarter": endOfQuarter(now),
        "Next Quarter": endOfQuarter(addQuarters(now, 1))
    };

    const selectedDate = dateMap[timeline];

    return selectedDate
        ? format(endOfDay(selectedDate), "yyyy-MM-dd")
        : null;
};

const AddIdeaBox = () => {
    const navigate = useNavigate();
    const { userInfo } = useAuth()
    const employeeID = userInfo?.employee_id || '';
    const agencyId = userInfo?.agencies?.id;

    const [selectedOutcomeName, setSelectedOutcomeName] = useState("");
    const [selectedOutcomeID, setSelectedOutcomeID] = useState("");
    const [impactLevelOptions, setImpactLevelOptions] = useState<Array<{ key: string, name: string }>>([])
    const [priority, setPriority] = useState("")
    const [loading, setLoading] = useState(false);
    const [skillOptions, setSkillOptions] = useState<
        { key: string; name: string }[]
    >([]);
    const [selectedSkills, setSelectedSkills] = useState<any[]>([]);

    // Validation schema
    const addIdeaSchema = yup.object().shape({
        title: yup.string()
            .test('not-only-whitespace', 'Please add a valid Title', value => !value || value.trim().length > 0)
            .required('This field is required'),
        suggestedTimeline: yup.string().nullable(),
        criticality: yup.string().required('Please select the criticality'),
        impactLevel: yup.string().required('Please select the Impact'),
        impactLevelID: yup.string().required('Please select the Impact'),
        messageAttachments: yup.array()
    });

    //api
    const { data: skillData, loading: skillLoading } = useQuery(GET_ALL_SKILLS, {
        fetchPolicy: 'no-cache',
    });

    const {
        loading: projectsDataLoading,
        data: projectsData,
    } = useQuery(PROJECTS_BY_EMP_ID_IDEA_BOX, {
        variables: {
            emp_id: employeeID,
            organization_id: agencyId
        },
        fetchPolicy: "no-cache",
        skip: !employeeID || !agencyId,
    });

    const {
        loading: climbsLoading,
        data: climsData,
    } = useQuery(GET_CLIMBS, {
        fetchPolicy: "no-cache"
    });

    const {
        loading: impactsLoading,
        data: impactsData,
    } = useQuery(GET_IMPACT_LEVELS, {
        fetchPolicy: "no-cache"
    });

    const [createIdea, { loading: createIdeaLoading }] = useMutation(CREATE_IDEA_GOAL, {
        fetchPolicy: 'no-cache',
    })

    //react hook form
    const methods = useForm<any>({
        resolver: yupResolver(addIdeaSchema),
        defaultValues: {
            criticality: "No"
        }
    })

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        trigger,
        control,
        formState: { errors },
    } = methods;

    //watch
    const watchSuggestedTimeline: string = watch('suggestedTimeline');
    const watchImpactLevel: string = watch('impactLevel');
    const watchCriticality: string = watch('criticality');

    useEffect(() => {
        if (skillData && skillData.get_all_skills?.skills?.length > 0) {
            const options = skillData.get_all_skills?.skills?.map((skill: any) => ({
                key: skill.id,
                name: skill?.skill_name
            }));
            setSkillOptions(options);
        }
    }, [skillData])

    useEffect(() => {
        if (impactsData && impactsData?.get_impact_levels?.status_code == 200) {
            const impacts = impactsData?.get_impact_levels?.impact_levels.map((i: any) => ({
                key: i.id,
                name: i.name,
            }));

            setImpactLevelOptions(impacts)
        }
    }, [impactsData])

    async function handleUpload(file: File) {
        const token = localStorage.getItem("authToken")!;
        return await uploadFileDirectly(file, file.name, token);
    };

    function handleSelectTimeline(key: number, name: string): void {
        // setValue('projectID', key)
        setValue('suggestedTimeline', name)
        // trigger('suggestedTimeline')
    }

    function handleSelectImpactLevel(key: string, name: string): void {
        // setValue('projectID', key)
        setValue('impactLevelID', key)
        setValue('impactLevel', name)
        trigger('impactLevel')

        if (watchCriticality === "Yes") {
            if (key === "1" || key === "2") setPriority("High");
            else if (key === "3" || key === "4") setPriority("Medium");
        } else if (watchCriticality === "No") {
            if (key === "1" || key === "2") setPriority("Medium");
            else if (key === "3" || key === "4") setPriority("Low");
        }
    }

    const handleSelectSkills = (key: any, name: any, type: any) => {
        const allOptions = skillOptions;

        if (key === "all") {
            if (name === true) {
                // Deselect all
                setSelectedSkills([]);
            } else {
                // Select all
                setSelectedSkills(allOptions);
            }
        } else {
            setSelectedSkills((prev) => {
                const exists = prev.some((item) => item.key == key);
                if (exists) {
                    // REMOVE if already selected
                    return prev.filter((item) => item.key != key);
                } else {
                    // ADD if not selected
                    return [...prev, { key, name, type }];
                }
            });
        }
    };

    const handleRemoveSkill = (key: string) => {
        setSelectedSkills((prev) => prev.filter((item) => item.key !== key));
    };

    async function onSubmit(data: any) {
        // atlease one skill should be there
        if (selectedSkills?.length < 1) {
            showError('Please Select Atleast One Skill')
            return
        }

        if (selectedOutcomeName === "") {
            showError("Please select CLIMB Outcome");
            return
        }

        const payload = {
            title: data?.title,
            criticality: data?.criticality,
            impactLevel: data?.impactLevel,
            priority: priority,
            climbOutcome: selectedOutcomeName,
            suggestedTimeline: data?.suggestedTimeline || "",
            employee_id: parseInt(employeeID),
            attachments: data?.titleAttachments || [],
            projectsList: projectsData?.projects_by_emp_id || []
        };

        try {
            setLoading(true);

            const response = await fetch(
                "https://n8n-rrjie-u41909.vm.elestio.app/webhook/0183cc6b-af63-4f47-a6f8-b5e680148e2d",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }

            const result = await response.text();
            // showSuccess(result);

            // Step 1: Parse the inner JSON string
            const parsedResult = JSON.parse(result);

            const skillIds = selectedSkills?.length > 0 ? selectedSkills?.map((s: any) => {
                return parseInt(s.key)
            }) : []

            const payloadForCreate = {
                user_input: data?.title,
                title: parsedResult?.new_title,
                project_id: parseInt(parsedResult?.project_id),
                goal_for: parsedResult?.goal_for,
                goals_static_id: parseInt(parsedResult?.goal_type_id),
                expected_closing_date: getExpectedClosingDate(data?.suggestedTimeline),
                outcome_id: parseInt(selectedOutcomeID),
                priority: priority,
                user_selected: data.criticality === "No" ? false : true,
                impact_level_id: parseInt(data?.impactLevelID),
                attachment_list: data?.titleAttachments || [],
                links: parsedResult?.urls || [],
                skill_ids: skillIds,
                created_by: parseInt(employeeID)
            }

            try {
                const response = await createIdea({ variables: { input: payloadForCreate } });
                if (response.data.create_idea_goal.status_code === 200) {
                    showSuccess(response.data.create_idea_goal.message)

                    navigate('/view_idea', {
                        state: {
                            ideaID: response.data.create_idea_goal?.idea_goal?.id
                        }
                    })
                }
                if (response.data.create_idea_goal.status_code === 201) {
                    showSuccess(response.data.create_idea_goal.message)

                    navigate('/view_idea', {
                        state: {
                            ideaID: response.data.create_idea_goal?.idea_goal?.id
                        }
                    })
                }
                if (response.data.create_idea_goal.status_code === 400) {
                    showError(response.data.create_idea_goal.message)
                }
            } catch (error) {
                if (error instanceof ApolloError) {
                    showError(error.message)
                }
            }
        } catch (error) {
            console.error("Failed to send to n8n:", error);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)} id="cts-respon-p-dv" className="ideabox-section w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start gap-[24px] py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope">
                    <div className="flex-1 flex flex-col items-start justify-start gap-6">
                        <BreadCumb />
                        <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
                            <img className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img" alt="" src={backArrow}
                                onClick={() => navigate(-1)}
                            />
                            <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Add Idea</h3>
                        </div>
                    </div>
                    <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
                        <div className="w-full relative flex flex-col items-start box-border text-left text-2xl text-gray font-manrope gap-[8px]">
                            <div className="self-stretch flex flex-row items-center justify-start gap-[12px] text-2xl">
                                <div className="submitidea-icon"><img className="w-12 relative h-12" src={AddIdea} alt="" /></div>
                                <div className="grid gap-[8px]">
                                    <h5 className="my-0 text-xl-dv flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">Submit Idea</h5>
                                    <span className="self-stretch relative text-dimgray">Share your idea and help the team understand its priority.</span>
                                </div>
                            </div>

                        </div>
                        <div className="grid grid-cols-1 mq1400:grid-1 mq1275:grid-cols-1 mq950:grid-cols-1 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                            <InputWithAttachments
                                name="title"
                                label="Message with attachments"
                                control={control}
                                uploadHandler={handleUpload}
                                placeholder="What do you want to achieve?"
                            />
                        </div>

                        {/* CLIMB Outcome */}
                        <div className="w-full mt-6">
                            <label className="block text-gray-700 text-sm font-bold mb-2">
                                CLIMB Outcome*
                            </label>
                            <div className="grid grid-cols-5 mq1400:grid-cols-5 mq1275:grid-cols-4 mq950:grid-cols-3 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
                                {climsData?.get_climbs?.climbs.map(
                                    (option: any) => (
                                        <div
                                            key={option?.id}
                                            onClick={() => {
                                                setSelectedOutcomeName(option?.name)
                                                setSelectedOutcomeID(option?.id)
                                            }}
                                            className={`border rounded-xl text-left flex justify-start p-[30px] items-center gap-[8px] ideas-box-blog cursor-pointer ${selectedOutcomeID == option?.id
                                                ? "bg-gray-50"
                                                : "border-gray-300"
                                                }`}
                                        >
                                            <div className="block">
                                                <h5 className="my-0 text-xl-dv self-stretch relative text-lg"> {option?.name}</h5>
                                                <p className="text-sm my-0 dark:text-white">{option?.description}</p>
                                            </div>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[16px] min-w-full">
                            <div className="grid">
                                <InputsDropdownMultipleSearchSelect
                                    label="Skills*"
                                    placeHolder="Choose"
                                    options={skillOptions}
                                    onSelect={handleSelectSkills}
                                    inputType="checkbox"
                                    checkedItems={selectedSkills}
                                />

                                {selectedSkills?.length > 0 && (
                                    <div className="reportmulselect-blg flex-1 w-full cts-flex1-remd contents flex-col items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg min-w-full">
                                        <div className="inner-reportmulselect-blg items-start justify-start text-left text-sm mq825:max-w-[100%] text-text-text-primary font-body-reg mt-2">
                                            {selectedSkills.map((ss: any) => (
                                                <div
                                                    key={ss?.key}
                                                    className="reportmulselect-blg-dv rounded-radius-md1 overflow-hidden flex flex-row items-center justify-start gap-[8px] inner-item-select-dv border-[1px] border-solid border-surface-border dark:bg-slate-700"
                                                >
                                                    <div className="flex flex-row items-center justify-start">
                                                        <div className="relative text-sm font-body-xs-semi-bold text-colors-text-black text-left inline-block dark:text-white">
                                                            {ss?.name}
                                                        </div>
                                                    </div>
                                                    <img
                                                        className="h-4 w-4 relative overflow-hidden shrink-0 cursor-pointer"
                                                        loading="lazy"
                                                        alt="Remove"
                                                        src={closeDowpDowniconimg}
                                                        onClick={() => handleRemoveSkill(ss.key)}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <SearchableInputsDropdown
                                label="Suggested Timeline"
                                initialValue={watchSuggestedTimeline}
                                placeHolder="Select Timeline"
                                options={timelineOptions}
                                onSelect={handleSelectTimeline}
                                errorMessage={errors?.suggestedTimeline}
                            />

                        </div>


                        {/* Criticality */}
                        <div className="w-full relative flex flex-col items-start box-border text-left text-2xl text-gray font-manrope gap-[8px] mt-6 quickly-section-ideablog">
                            <h4 className="text-lg font-semibold mb-2">
                                Let's quickly figure out how critical this is.
                            </h4>
                            <p className="text-sm text-gray-500 mb-3">
                                Helping your team know when to act. This won't take more than 30
                                seconds.
                            </p>

                            <div className="flex flex-col gap-2">
                                {[
                                    {
                                        value: "Yes",
                                        label: "Yes - It's affecting users/teams immediately",
                                    },
                                    {
                                        value: "No",
                                        label: "No - It can wait a little",
                                    },
                                ].map((opt) => (
                                    <label
                                        key={opt.value}
                                        className={`border rounded-lg p-3 cursor-pointer ${watchCriticality === opt.value
                                            ? "quickly-section-ideablog-item"
                                            : "border-gray-300 quickly-section-bdr-item"
                                            }`}
                                    >
                                        <input
                                            type="radio"
                                            value={opt.value}
                                            checked={watchCriticality === opt.value}
                                            onChange={() => {
                                                // Update the form value
                                                setValue("criticality", opt.value);
                                                trigger("criticality");

                                                // If impactLevel is already selected, apply the same priority logic
                                                if (watchImpactLevel) {
                                                    const selectedImpact = impactLevelOptions && impactLevelOptions.find(
                                                        (opt2) => opt2.name === watchImpactLevel
                                                    );
                                                    if (selectedImpact) {
                                                        const key = selectedImpact.key;

                                                        if (opt.value === "Yes") {
                                                            if (key === "1" || key === "2") setPriority("High");
                                                            else if (key === "3" || key === "4") setPriority("Medium");
                                                        } else if (opt.value === "No") {
                                                            if (key === "1" || key === "2") setPriority("Medium");
                                                            else if (key === "3" || key === "4") setPriority("Low");
                                                        }
                                                    }
                                                }
                                            }}
                                            className="mr-2"
                                        />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                            {errors.criticality && (
                                <p className="text-red-600 text-sm mt-1">
                                    {typeof errors.criticality.message === 'string' ? errors.criticality.message : 'Invalid input'}
                                </p>
                            )}
                        </div>

                        <SearchableInputsDropdown
                            label="What happens if we don't do this soon?"
                            initialValue={watchImpactLevel}
                            placeHolder="Select Impact Level"
                            options={impactLevelOptions}
                            onSelect={handleSelectImpactLevel}
                            errorMessage={errors?.impactLevel}
                        />

                        <div className="w-full relative rounded-xl quickly-section-bdr-item blog-darktheme-bdr border-solid border-[2px] box-border flex flex-col items-start p-4 gap-2 text-left text-xs text-white font-manrope">
                            <div className="rounded-[9999px] bg-crimson flex items-center ">
                                <b className={`relative leading-4 ${getPriorityClass(priority)}`}>
                                    {priority}
                                </b>
                            </div>
                            <div className="relative text-sm text-dimgray">You can change this anytime. Your team will also be able to update it together.</div>
                        </div>

                        <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px] mq950:box-border mq1425:flex-wrap  mq1425:box-border mq450:box-border">
                            <button className="cursor-pointer bg-white py-2.5 h-[44px] mq575:h-[35px] px-[42px] btn-text-purple mq575:py-[12.5px] mq575:px-[25px] rounded-radiuscomponent-button box-border flex flex-row items-center justify-center gap-[12px] border-[2px] border-solid border-surface-primary-surface hover:bg-gainsboro-100 hover:box-border hover:border-[2px] hover:border-solid hover:border-dodgerblue"
                                // onClick={handleGoBack}
                                type="button"
                                onClick={() => navigate(-1)}
                            >
                                <div className="relative text-sm font-body-xs-semi-bold  dark:text-white text-center inline-block">
                                    Cancel
                                </div>
                            </button>
                            <button className={`cursor-pointer [border:none] py-2.5 h-[44px] mq575:h-[35px] px-[42px] mq575:py-[12.5px] mq575:px-[25px] bg-surface-primary-surface flex flex-row items-center justify-center gap-[12px] hover:bg-dodgerblue`}
                                // disabled={(err || careDelErr) !== ''}
                                type="submit"
                            >
                                <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary text-center inline-block">
                                    Save
                                </div>
                            </button>
                        </div>
                    </div>
                </form>
            </FormProvider>
            {(loading || climbsLoading || impactsLoading || projectsDataLoading || createIdeaLoading || skillLoading) && (
                <Loader />
            )}
        </>
    )
}

export default AddIdeaBox;